import { useRef, useCallback } from "react";

interface CodeEditorProps {
  value: string;
  onChange: (value: string) => void;
  language: string;
  disabled?: boolean;
  onPasteDetected?: () => void;
}

export function CodeEditor({ value, onChange, language, disabled, onPasteDetected }: CodeEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const pastedText = e.clipboardData.getData("text");
      if (pastedText.length > 80 || pastedText.split("\n").length > 3) {
        onPasteDetected?.();
      }
    },
    [onPasteDetected]
  );

  const lines = value.split("\n");
  const lineCount = Math.max(lines.length, 10);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Tab") {
        e.preventDefault();
        const target = e.currentTarget;
        const start = target.selectionStart;
        const end = target.selectionEnd;
        const newValue = value.substring(0, start) + "  " + value.substring(end);
        onChange(newValue);
        requestAnimationFrame(() => {
          target.selectionStart = target.selectionEnd = start + 2;
        });
      }
    },
    [value, onChange]
  );

  const handleScroll = useCallback(() => {
    const textarea = textareaRef.current;
    const lineNumbers = textarea?.previousElementSibling as HTMLElement;
    if (textarea && lineNumbers) {
      lineNumbers.scrollTop = textarea.scrollTop;
    }
  }, []);

  const langLabel: Record<string, string> = {
    python: "Python",
    sql: "SQL",
    power_bi: "DAX",
  };

  return (
    <div className="rounded-lg border border-border overflow-hidden bg-[#1e1e2e]">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-[#181825] border-b border-border/30">
        <div className="flex items-center gap-2">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-500/70" />
            <div className="w-3 h-3 rounded-full bg-yellow-500/70" />
            <div className="w-3 h-3 rounded-full bg-green-500/70" />
          </div>
          <span className="text-xs text-muted-foreground ml-2 font-mono">
            {langLabel[language] || language}
          </span>
        </div>
      </div>

      {/* Editor */}
      <div className="relative flex min-h-[300px] max-h-[500px]">
        {/* Line numbers */}
        <div
          className="flex-shrink-0 py-3 px-2 text-right select-none overflow-hidden bg-[#181825] border-r border-border/20"
          style={{ width: "3rem" }}
        >
          {Array.from({ length: lineCount }, (_, i) => (
            <div
              key={i}
              className="text-[13px] leading-[1.6] font-mono text-muted-foreground/40"
            >
              {i + 1}
            </div>
          ))}
        </div>

        {/* Textarea */}
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onScroll={handleScroll}
          onPaste={handlePaste}
          disabled={disabled}
          spellCheck={false}
          className="flex-1 bg-transparent text-[13px] leading-[1.6] font-mono text-emerald-300 p-3 resize-none outline-none overflow-auto placeholder:text-muted-foreground/30 disabled:opacity-50"
          placeholder={`// Escreva seu código ${langLabel[language] || ""} aqui...`}
        />
      </div>
    </div>
  );
}
